import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, Check, Send, ShieldCheck } from "lucide-react";
import CompletionPhotos from "@/components/CompletionPhotos";
import PropertyBrief from "@/components/PropertyBrief";

// Cleaner-side turnover job: private access details, checklist and completion photos.
export default function TurnoverJobPanel({ booking, onDone }) {
  const [access, setAccess] = useState(null);
  const [checklist, setChecklist] = useState(booking.checklist || []);
  const [busy, setBusy] = useState(false);
  const live = booking.status === "confirmed";

  useEffect(() => {
    api.get(`/bookings/${booking.booking_id}/access`)
      .then((r) => setAccess(r.data))
      .catch(() => setAccess({ visible: false }));
  }, [booking.booking_id]);

  const tick = async (item) => {
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/checklist`, { item_id: item.id, done: !item.done });
      setChecklist(data.checklist);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/bookings/submit", { booking_id: booking.booking_id });
      toast.success("Sent to the host for approval");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const accessFields = access?.access || {};
  const hasAccess = Object.values(accessFields).some(Boolean);
  const doneCount = checklist.filter((i) => i.done).length;

  return (
    <div className="mt-4 border-t border-border pt-4 space-y-5" data-testid={`turnover-panel-${booking.booking_id}`}>
      <PropertyBrief summary={booking.property_summary} turnover={booking.turnover} />

      <div>
        <h4 className="text-sm font-semibold flex items-center gap-1.5"><KeyRound className="h-4 w-4 text-accent" /> Property access</h4>
        {access === null ? <Loader2 className="h-4 w-4 animate-spin text-accent mt-2" /> : !access.visible ? (
          <p className="text-sm text-muted-foreground mt-1" data-testid={`access-hidden-${booking.booking_id}`}>
            {access.reason || "Access details are no longer available for this booking."}
          </p>
        ) : !hasAccess ? (
          <p className="text-sm text-muted-foreground mt-1" data-testid={`access-empty-${booking.booking_id}`}>The host hasn't added access details. Message them to arrange entry.</p>
        ) : (
          <div className="mt-2 p-4 rounded-xl bg-accent/5 border border-accent/20 space-y-2 text-sm" data-testid={`access-details-${booking.booking_id}`}>
            {accessFields.key_safe_location && <Line k="Key safe" v={accessFields.key_safe_location} />}
            {accessFields.lockbox_code && <Line k="Lockbox code" v={accessFields.lockbox_code} />}
            {accessFields.smart_lock_instructions && <Line k="Smart lock" v={accessFields.smart_lock_instructions} />}
            {accessFields.alarm_instructions && <Line k="Alarm" v={accessFields.alarm_instructions} />}
            {accessFields.entry_notes && <Line k="Entry notes" v={accessFields.entry_notes} />}
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1"><ShieldCheck className="h-3.5 w-3.5" /> Private to you · hidden automatically once this job is complete.</p>
          </div>
        )}
      </div>

      {checklist.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold">Turnover checklist <span className="text-muted-foreground font-normal">({doneCount}/{checklist.length})</span></h4>
          <div className="mt-2 space-y-1.5" data-testid={`checklist-${booking.booking_id}`}>
            {checklist.map((item) => (
              <button key={item.id} onClick={() => live && tick(item)} disabled={!live}
                className={`w-full flex items-center gap-3 p-2.5 rounded-xl border text-left text-sm transition-colors ${item.done ? "border-accent/40 bg-accent/5" : "border-border hover:border-accent/40"} disabled:cursor-default`}
                data-testid={`check-${booking.booking_id}-${item.id}`}>
                <span className={`h-5 w-5 rounded-md flex items-center justify-center border ${item.done ? "bg-accent border-accent text-white" : "border-border"}`}>
                  {item.done && <Check className="h-3.5 w-3.5" />}
                </span>
                <span className={item.done ? "line-through text-muted-foreground" : ""}>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold">Completion photos</h4>
        <div className="mt-2"><CompletionPhotos bookingId={booking.booking_id} canUpload={live} /></div>
      </div>

      {live ? (
        <button onClick={submit} disabled={busy} className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`submit-turnover-${booking.booking_id}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit for host approval
        </button>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid={`awaiting-approval-note-${booking.booking_id}`}>Submitted — waiting for the host to review your photos and approve.</p>
      )}
    </div>
  );
}

function Line({ k, v }) {
  return <p><span className="text-muted-foreground">{k}:</span> <strong className="font-medium">{v}</strong></p>;
}
